import { addMonths, differenceInCalendarMonths, differenceInHours, endOfMonth, format, getMonth, isValid, parse, parseISO, startOfMonth } from "date-fns";
import { nb } from "date-fns/locale/nb";

import { TZDate } from "@date-fns/tz";
import { colors } from "@ntemarked/mittne-components";

import {
	Maanedsvolum,
	Sikring,
	SikringFlyt,
	SikringMaalepunkt,
	SikringOmfangType,
	Sikringsordre,
	Sikringsordrestatus,
	ValidationError,
} from "pages/Sikring/types";

export const NOT_AVAILABLE = "Ikke tilgjengelig";

export function isDefined<T>(v: T | null | undefined): v is T {
	return v !== null && v !== undefined;
}

export function formatNumber(n: number | null | undefined): string {
	if (!isDefined(n)) return NOT_AVAILABLE;
	return Number(n).toLocaleString("nb-NO");
}

/**
 * Enheten alle priser på en sikring oppgis i. Backend lagrer innpris, fortjeneste og pris som
 * rene tall uten enhet, så den er en ren visningskonvensjon og må stå likt overalt.
 */
export const PRICE_UNIT = "øre/kWh";

/** Pris med enhet. Bruk denne overalt et prisbeløp vises for en saksbehandler. */
export function fmtPriceWithUnit(n: number | null | undefined): string {
	if (!isDefined(n)) return "–";
	return `${fmtPrice(n)} ${PRICE_UNIT}`;
}

export function fmtPrice(n: number | null | undefined): string {
	if (!isDefined(n)) return "–";
	return Number(n).toLocaleString("nb-NO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Formaterer en ren dato (yyyy-MM-dd). Brukes på sikringens start og slutt, som ikke har klokkeslett. */
export function fmtDate(isoDate: string | null | undefined): string {
	const date = isoDate ? parseISO(isoDate) : null;
	return date && isValid(date) ? format(date, "dd.MM.yyyy") : "–";
}

export function fmtDateTime(iso: string | null | undefined): string {
	const date = iso ? parseISO(iso) : null;
	return date && isValid(date) ? format(date, "dd.MM.yyyy HH:mm") : "–";
}

/** Bare datodelen av et tidspunkt (LocalDateTime), for tabeller der klokkeslettet tar for mye plass. */
export function fmtDateOnly(iso: string | null | undefined): string {
	return fmtDate(iso);
}

// Hele strengen må være et tall, så "12abc" og "1,5,3" avvises i stedet for å leses som 12 og 1.5. Punktum godtas
// ikke som desimalskilletegn, siden "45.000" ellers ville blitt lest som 45 av en som mente 45 000.
const NORWEGIAN_NUMBER = /^-?(\d+(,\d+)?|,\d+)$/;

export function parseNorwegianNumber(s: string | null | undefined): number | null {
	if (!s) return null;
	const trimmed = String(s).replace(/\s/g, "");
	return NORWEGIAN_NUMBER.test(trimmed) ? Number(trimmed.replace(",", ".")) : null;
}

export const STATUS_LABEL: Record<Sikringsordrestatus, string> = {
	[Sikringsordrestatus.AwaitingSignature]: "Venter på signering",
	[Sikringsordrestatus.AwaitingContract]: "Venter på kontrakt",
	[Sikringsordrestatus.AwaitingSecureVolume]: "Venter på UtilityCloud",
	[Sikringsordrestatus.ContractFailed]: "Kontrakt feilet",
	[Sikringsordrestatus.Failed]: "Feilet",
	[Sikringsordrestatus.Completed]: "Fullført",
	[Sikringsordrestatus.Deleted]: "Slettet",
	[Sikringsordrestatus.PartiallyDeleted]: "Delvis slettet",
};

export const STATUS_DESCRIPTION: Record<Sikringsordrestatus, string> = {
	[Sikringsordrestatus.AwaitingSignature]: "Venter på at kunden signerer.",
	[Sikringsordrestatus.AwaitingContract]: "Venter på at UtilityCloud oppretter kontrakten.",
	[Sikringsordrestatus.AwaitingSecureVolume]: "Uten kontrakt - venter på at sikringen postes og verifiseres i UtilityCloud.",
	[Sikringsordrestatus.ContractFailed]: "UtilityCloud klarte ikke å opprette kontrakten.",
	[Sikringsordrestatus.Failed]: "Innsending eller verifisering mot UtilityCloud feilet.",
	[Sikringsordrestatus.Completed]: "Alle sikringene er opprettet og verifisert i UtilityCloud.",
	[Sikringsordrestatus.Deleted]: "Sikringsordren er slettet, og sikringene slettes i UtilityCloud.",
	[Sikringsordrestatus.PartiallyDeleted]:
		"Sikringsordren er slettet, men sikringene for måneder tilbake i tid kan ikke slettes og er beholdt i UtilityCloud.",
};

/** Backend sletter bare sikringsordrer som er ferdig behandlet, og svarer 409 for resten. */
const DELETABLE_STATUSES: ReadonlySet<Sikringsordrestatus> = new Set([Sikringsordrestatus.Completed, Sikringsordrestatus.Failed]);

export function isDeletable(status: Sikringsordrestatus): boolean {
	return DELETABLE_STATUSES.has(status);
}

/** Slettet helt, eller delvis med secure volumes tilbake i tid beholdt. Ingen av dem kan slettes på nytt. */
const DELETED_STATUSES: ReadonlySet<Sikringsordrestatus> = new Set([Sikringsordrestatus.Deleted, Sikringsordrestatus.PartiallyDeleted]);

export function isDeleted(status: Sikringsordrestatus): boolean {
	return DELETED_STATUSES.has(status);
}

/** Kortere etiketter for tabellen, der det er trangt. Detaljvisning og statusfilter bruker STATUS_LABEL. */
export const STATUS_LABEL_SHORT: Record<Sikringsordrestatus, string> = {
	...STATUS_LABEL,
	[Sikringsordrestatus.AwaitingSignature]: "Venter",
};

interface BadgeColors {
	background: string;
	color: string;
}

const GREEN: BadgeColors = { background: colors.green200, color: colors.green800 };
const BLUE: BadgeColors = { background: colors.blue300, color: colors.blue800 };
const YELLOW: BadgeColors = { background: colors.yellow, color: colors.grey900 };
const RED: BadgeColors = { background: colors.coral200, color: colors.coral };
const GREY: BadgeColors = { background: colors.grey200, color: colors.grey800 };

export const STATUS_COLORS: Record<Sikringsordrestatus, BadgeColors> = {
	[Sikringsordrestatus.AwaitingSignature]: YELLOW,
	[Sikringsordrestatus.AwaitingContract]: BLUE,
	[Sikringsordrestatus.AwaitingSecureVolume]: BLUE,
	[Sikringsordrestatus.ContractFailed]: RED,
	[Sikringsordrestatus.Failed]: RED,
	[Sikringsordrestatus.Completed]: GREEN,
	[Sikringsordrestatus.Deleted]: GREY,
	[Sikringsordrestatus.PartiallyDeleted]: GREY,
};

export const FLYT_LABEL: Record<SikringFlyt, string> = {
	[SikringFlyt.SigneringKontraktSikring]: "Signering, kontrakt og sikring",
	[SikringFlyt.KontraktSikring]: "Kontrakt og sikring (uten signering)",
	[SikringFlyt.KunSikring]: "Kun sikring (uten signering og kontrakt)",
};

/** Kortere etiketter for tabellen, der flyten står under statusen. */
export const FLYT_LABEL_SHORT: Record<SikringFlyt, string> = {
	[SikringFlyt.SigneringKontraktSikring]: "Signering og kontrakt",
	[SikringFlyt.KontraktSikring]: "Uten signering",
	[SikringFlyt.KunSikring]: "Kun sikring",
};

/** Hva flyten betyr for ordren. Vises når flyten velges og i oppsummeringen. */
export const FLYT_INFO: Record<SikringFlyt, string> = {
	[SikringFlyt.SigneringKontraktSikring]: "Ordren må signeres av kunden før sikringen sendes til UtilityCloud.",
	[SikringFlyt.KontraktSikring]: "Uten signering: kontrakten opprettes med en gang, og sikringen sendes til UtilityCloud.",
	[SikringFlyt.KunSikring]:
		"Uten signering og kontrakt: sikringen sendes til UtilityCloud med en gang. Kunden må allerede ha aktiv kontrakt, og det gjøres ingen kredittsjekk.",
};

interface ScopeLike {
	omfangType: SikringOmfangType;
	prisomraade?: string | null;
}

export const SCOPE_LABEL: Record<SikringOmfangType, string> = {
	[SikringOmfangType.Maalepunkt]: "Ett målepunkt",
	[SikringOmfangType.Kunde]: "Kundenivå",
};

export function scopeShort(s: ScopeLike): string {
	const label = SCOPE_LABEL[s.omfangType];
	return s.omfangType === SikringOmfangType.Maalepunkt ? label : `${label} (${s.prisomraade ?? "–"})`;
}

export function scopeFull(s: ScopeLike): string {
	// Målepunkt-IDen ligger på ordren, ikke på sikringen, så den kan ikke vises herfra.
	const label = SCOPE_LABEL[s.omfangType];
	return s.omfangType === SikringOmfangType.Maalepunkt ? label : `${label} i prisområde ${s.prisomraade ?? "–"}`;
}

/** Kort visning av ordrens målepunkt: ID-en når det er ett, ellers antallet. Null når ordren ikke har noen. */
export function formatMaalepunkter(maalepunkter: string[] | null): string | null {
	if (!maalepunkter?.length) return null;
	return maalepunkter.length === 1 ? maalepunkter[0] : `${formatNumber(maalepunkter.length)} målepunkt`;
}

/** Samlet sikret volum: summen av månedsvolumene når de finnes, ellers totalvolumet på sikringen. */
export function totalVolumeKwh(order: Pick<Sikringsordre, "sikring" | "maanedsvolumer">): number | null {
	if (order.maanedsvolumer?.length) return order.maanedsvolumer.reduce((sum, m) => sum + m.volumKwh, 0);
	const perMonth = order.sikring.volumPerMaanedKwh;
	if (perMonth && Object.keys(perMonth).length > 0) return Object.values(perMonth).reduce((sum, v) => sum + v, 0);
	return order.sikring.volumKwh;
}

export function sortMonthlyVolumes(maanedsvolumer: Maanedsvolum[]): Maanedsvolum[] {
	return [...maanedsvolumer].sort((a, b) => a.maaned.localeCompare(b.maaned));
}

/** Månedsnøkkelen brukt i veiviseren og i volumPerMaanedKwh, samme format som <input type="month">. */
const MONTH_KEY_FORMAT = "yyyy-MM";
const ISO_DATE_FORMAT = "yyyy-MM-dd";
/** «jan. 2026». Locale sendes eksplisitt, så formatet ikke avhenger av setDefaultOptions i index.tsx (som tester ikke kjører). */
const MONTH_LABEL_FORMAT = "MMM yyyy";

/** Tolker en yyyy-MM-nøkkel som første dag i måneden, lokal tid. Gir Invalid Date for en ugyldig nøkkel. */
function parseMonthKey(monthKey: string): Date {
	return parse(monthKey, MONTH_KEY_FORMAT, new Date());
}

/** yyyy-MM-nøkkelen for måneden datoen ligger i. */
export function toMonthKey(date: Date): string {
	return format(date, MONTH_KEY_FORMAT);
}

export interface Month {
	key: string;
	label: string;
}

/** Formaterer en yyyy-MM-nøkkel som «jan. 2026». */
export function monthLabel(key: string): string {
	const month = parseMonthKey(key);
	return isValid(month) ? format(month, MONTH_LABEL_FORMAT, { locale: nb }) : key;
}

/** Korteste og lengste sikringsperiode veiviseren tillater: én måned til tre år, eller fem år uten kontrakt. */
export const MIN_SIKRING_MONTHS = 1;
export const MAX_SIKRING_MONTHS = 36;
export const MAX_SIKRING_MONTHS_NO_CONTRACT = 60;

/** Lengste sikringsperiode i måneder. Uten kontrakt tillates en lengre periode. */
export function maxSikringMonths(skipContract: boolean): number {
	return skipContract ? MAX_SIKRING_MONTHS_NO_CONTRACT : MAX_SIKRING_MONTHS;
}

/** Antall kalendermåneder i perioden, begge inklusive. Nøkler på formatet yyyy-MM. */
export function countMonths(startMonthKey: string, endMonthKey: string): number {
	const start = parseMonthKey(startMonthKey);
	const end = parseMonthKey(endMonthKey);
	if (!isValid(start) || !isValid(end)) return 0;
	return differenceInCalendarMonths(end, start) + 1;
}

interface OverlapCriteria {
	orgnr: string;
	scope: SikringOmfangType;
	maalepunktId: string;
	prisomraade: string;
	startMonth: string;
	endMonth: string;
}

/**
 * Eksisterende sikringer på samme omfang med en periode som overlapper den valgte. Samme omfang er samme
 * målepunkt, eller kundenivå for samme bedrift i samme prisområde. Feilede sikringer ble aldri secure volumes
 * og teller ikke, og det gjør heller ikke slettede.
 */
export function findOverlappingOrders(orders: Sikringsordre[], criteria: OverlapCriteria): Sikringsordre[] {
	const start = firstDayOfMonth(criteria.startMonth);
	const end = lastDayOfMonth(criteria.endMonth);
	if (!start || !end) return [];
	return orders.filter((o) => {
		if (
			o.status === Sikringsordrestatus.Failed ||
			o.status === Sikringsordrestatus.ContractFailed ||
			isDeleted(o.status) ||
			o.sikring.omfangType !== criteria.scope
		)
			return false;
		const sameScope =
			criteria.scope === SikringOmfangType.Maalepunkt
				? !!criteria.maalepunktId && (o.maalepunkter ?? []).includes(criteria.maalepunktId)
				: !!criteria.orgnr && o.kunde?.organisasjonsnummer === criteria.orgnr && o.sikring.prisomraade === criteria.prisomraade;
		// yyyy-MM-dd kan sammenlignes som tekst. Begge perioder er inklusive i begge ender.
		return sameScope && o.sikring.start <= end && o.sikring.slutt >= start;
	});
}

/** Identifiserer et sett med overlappende sikringer, slik at en bekreftelse gjelder akkurat de sikringene. */
export function overlapKey(overlaps: Sikringsordre[]): string {
	return overlaps
		.map((o) => o.id)
		.sort((a, b) => a.localeCompare(b))
		.join(",");
}

/** Siste til og med-måned som holder perioden innenfor maxSikringMonths. Tom streng for en ugyldig nøkkel. */
export function maxEndMonth(startMonthKey: string, skipContract: boolean): string {
	const start = parseMonthKey(startMonthKey);
	return isValid(start) ? toMonthKey(addMonths(start, maxSikringMonths(skipContract) - 1)) : "";
}

/** Feilmelding for perioden, eller null når den er gyldig. */
export function periodError(startMonthKey: string, endMonthKey: string, skipContract: boolean): string | null {
	if (!isValid(parseMonthKey(startMonthKey)) || !isValid(parseMonthKey(endMonthKey))) return "Velg både fra og med- og til og med-måned.";
	const monthCount = countMonths(startMonthKey, endMonthKey);
	if (monthCount < MIN_SIKRING_MONTHS) return "Til og med-måneden kan ikke være før fra og med-måneden.";
	const maxMonths = maxSikringMonths(skipContract);
	if (monthCount > maxMonths) return `Perioden kan være maks ${maxMonths / 12} år (${maxMonths} måneder). Valgt periode er ${monthCount} måneder.`;
	return null;
}

/**
 * Feilmelding for sikringsmarginen, eller null når den er gyldig. Gir også null så lenge marginen ikke kan
 * beregnes ennå (kjøpspris eller kundepris mangler) - at feltene er fylt ut sjekkes i getMissingFieldsForStep3.
 */
export function marginError(fortjeneste: number | null): string | null {
	if (isDefined(fortjeneste) && fortjeneste < 0) return "Kundeprisen er lavere enn kjøpsprisen.";
	return null;
}

/** Om sikringen starter før inneværende måned. En start i inneværende måned regnes ikke som tilbake i tid. */
export function isBackInTime(startMonthKey: string, today: Date = new Date()): boolean {
	const start = parseMonthKey(startMonthKey);
	return isValid(start) && start < startOfMonth(today);
}

/** Første måned sikringen med kontrakt kan starte: inneværende måned, selv om den 1. er passert. */
export function earliestStartMonth(today: Date = new Date()): string {
	return toMonthKey(startOfMonth(today));
}

/** Med kontrakt kan sikringen ikke starte før inneværende måned. Feilmelding, eller null når starten er gyldig. */
export function backInTimeError(startMonthKey: string, skipContract: boolean, today: Date = new Date()): string | null {
	if (skipContract || !isBackInTime(startMonthKey, today)) return null;
	return "Med kontrakt kan sikringen ikke starte før inneværende måned.";
}

/**
 * Lister ut hver kalendermåned i perioden. Både start og slutt er inklusive, siden backend
 * krever at sikringen starter første dag i en måned og slutter siste dag i en måned.
 */
export function listMonths(start: string, end: string): Month[] {
	const result: Month[] = [];
	const startDate = parseISO(start);
	const endDate = parseISO(end);
	if (!isValid(startDate) || !isValid(endDate) || startDate > endDate) return result;
	const last = startOfMonth(endDate);
	for (let current = startOfMonth(startDate); current <= last && result.length < MAX_SIKRING_MONTHS_NO_CONTRACT; current = addMonths(current, 1)) {
		result.push({ key: toMonthKey(current), label: format(current, MONTH_LABEL_FORMAT, { locale: nb }) });
	}
	return result;
}

/** Første dag i måneden til en yyyy-MM-nøkkel, som yyyy-MM-dd. Tom streng for en ugyldig nøkkel. */
export function firstDayOfMonth(monthKey: string): string {
	const month = parseMonthKey(monthKey);
	return isValid(month) ? format(startOfMonth(month), ISO_DATE_FORMAT) : "";
}

/** Siste dag i måneden til en yyyy-MM-nøkkel, som yyyy-MM-dd. Tom streng for en ugyldig nøkkel. */
export function lastDayOfMonth(monthKey: string): string {
	const month = parseMonthKey(monthKey);
	return isValid(month) ? format(endOfMonth(month), ISO_DATE_FORMAT) : "";
}

/** yyyy-MM-nøkkelen en yyyy-MM-dd-dato hører til. En ugyldig dato returneres uendret. */
export function monthKeyOf(isoDate: string): string {
	const date = parseISO(isoDate);
	return isValid(date) ? toMonthKey(date) : isoDate;
}

/** Hvordan et totalvolum fordeles på månedene i perioden. */
export enum TotalDistribution {
	/** Etter STANDARD_PROFILE. Månedsvolumene beregnes her og sendes som volumPerMaanedKwh. */
	Standard = "STANDARD",
	/** Likt på alle timene i perioden. Månedsvolumene beregnes her etter antall timer og sendes som volumPerMaanedKwh. */
	Flat = "FLAT",
}

/**
 * Standard månedsprofil for fordeling av et totalvolum, i prosent, januar til desember.
 * Summerer til 99,99 % på grunn av avrunding - vektene normaliseres uansett, så det gjør ikke noe.
 */
export const STANDARD_PROFILE = [11.61, 10.96, 10.32, 7.74, 6.99, 5.38, 5.18, 5.8, 6.5, 8.49, 9.67, 11.35];

/** Standardprofilens prosent for kalendermåneden. */
function standardProfileWeight(month: Month): number {
	const date = parseMonthKey(month.key);
	return isValid(date) ? STANDARD_PROFILE[getMonth(date)] : 0;
}

/**
 * Hver måneds andel av volumet i perioden, i prosent (yyyy-MM → prosent). For et helt kalenderår er dette
 * standardprofilen selv; for kortere perioder er andelene normalisert, så de summerer til 100 %.
 */
export function standardProfileShares(months: Month[]): Record<string, number> {
	const weightSum = months.reduce((sum, m) => sum + standardProfileWeight(m), 0);
	if (weightSum === 0) return {};
	return Object.fromEntries(months.map((m) => [m.key, (standardProfileWeight(m) / weightSum) * 100]));
}

/** Andelen av kundens forventede forbruk sikringen kan dekke før saksbehandler får varsel om at volumet er høyt. */
export const WARN_SHARE_OF_EXPECTED_CONSUMPTION = 0.75;

export interface ConsumptionCheck {
	/** Forventet forbruk i perioden, summert over alle målepunktene. */
	expectedKwh: number;
	/** Sikret volum som andel av expectedKwh. 0,8 betyr 80 %. */
	share: number;
}

/**
 * Sammenligner sikret volum med kundens forventede forbruk i perioden. Årsforbruket fordeles på månedene etter
 * standardprofilen (ikke normalisert over perioden som i standardProfileShares), så en vinterperiode forventer
 * mer forbruk enn en like lang sommerperiode. Null når det ikke er noe å sammenligne med - også når et av
 * målepunktene mangler forventet årsforbruk, siden forbruket da ville vært undervurdert.
 */
export function checkVolumeAgainstConsumption(volumKwh: number | null, months: Month[], meteringPoints: SikringMaalepunkt[]): ConsumptionCheck | null {
	if (meteringPoints.some((mp) => !isDefined(mp.forventetAarsforbrukKwh))) return null;
	const annualKwh = meteringPoints.reduce((sum, mp) => sum + (mp.forventetAarsforbrukKwh ?? 0), 0);
	const profileSum = STANDARD_PROFILE.reduce((sum, p) => sum + p, 0);
	const expectedKwh = months.reduce((sum, m) => sum + (annualKwh * standardProfileWeight(m)) / profileSum, 0);
	if (!isDefined(volumKwh) || volumKwh <= 0 || expectedKwh <= 0) return null;
	return { expectedKwh, share: volumKwh / expectedKwh };
}

export function fmtPercent(n: number | null | undefined): string {
	if (!isDefined(n)) return "–";
	return `${n.toLocaleString("nb-NO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;
}

/**
 * Fordeler et totalvolum på månedene i perioden etter standardprofilen. Vektene normaliseres over
 * månedene som faktisk er med, så summen blir totalvolumet også for perioder som ikke er et helt år.
 * Hver måned får hele kWh etter største-rest-metoden: alle rundes ned, og de gjenværende kWh-ene går til
 * månedene med størst rest. Slik blir ingen måned negativ. En eventuell desimaldel av totalvolumet legges
 * på måneden med størst andel.
 */
export function distributeByStandardProfile(totalKwh: number, months: Month[]): Record<string, number> {
	if (months.length === 0) return {};
	const weights = months.map(standardProfileWeight);
	const weightSum = weights.reduce((sum, w) => sum + w, 0);
	const wholeKwh = Math.floor(totalKwh);
	const exact = weights.map((w) => (wholeKwh * w) / weightSum);
	const volumes = exact.map(Math.floor);
	const leftover = wholeKwh - volumes.reduce((sum, v) => sum + v, 0);
	exact
		.map((e, i) => ({ i, rest: e - volumes[i] }))
		.sort((a, b) => b.rest - a.rest)
		.slice(0, leftover)
		.forEach(({ i }) => volumes[i]++);
	const largest = weights.indexOf(Math.max(...weights));
	volumes[largest] = Math.round((volumes[largest] + totalKwh - wholeKwh) * 1000) / 1000;
	return Object.fromEntries(months.map((m, i) => [m.key, volumes[i]]));
}

/**
 * Antall timer i måneden i norsk tid, regnet fra månedsstart til neste månedsstart i Europe/Oslo. Sommertid gir
 * mars én time mindre og oktober én time mer enn antall dager × 24. Svaret er det samme uansett hvilken tidssone
 * nettleseren står i.
 */
export function hoursInMonth(month: Month): number {
	const date = parseMonthKey(month.key);
	if (!isValid(date)) return 0;
	const start = new TZDate(date.getFullYear(), date.getMonth(), 1, "Europe/Oslo");
	return differenceInHours(addMonths(start, 1), start);
}

/** Hver måneds andel av timene i perioden, i prosent (yyyy-MM → prosent). */
export function hourShares(months: Month[]): Record<string, number> {
	const totalHours = months.reduce((sum, m) => sum + hoursInMonth(m), 0);
	if (totalHours === 0) return {};
	return Object.fromEntries(months.map((m) => [m.key, (hoursInMonth(m) / totalHours) * 100]));
}

/**
 * Flat fordeling: sikringen beregnes per time, så totalvolumet fordeles på månedene etter antall timer, slik at hver
 * time får like mye. Hver måned får hele kWh, rundet ned. Det som blir til overs etter avrundingen, også en eventuell
 * desimaldel av totalvolumet, legges på februar - eller siste måned når perioden ikke har februar.
 */
export function distributeByHours(totalKwh: number, months: Month[]): Record<string, number> {
	const totalHours = months.reduce((sum, m) => sum + hoursInMonth(m), 0);
	if (totalHours === 0) return {};
	const wholeKwh = Math.floor(totalKwh);
	const volumes = months.map((m) => Math.floor((wholeKwh * hoursInMonth(m)) / totalHours));
	const leftover = totalKwh - volumes.reduce((sum, v) => sum + v, 0);
	const february = months.findIndex((m) => getMonth(parseMonthKey(m.key)) === 1);
	const leftoverIndex = february === -1 ? months.length - 1 : february;
	volumes[leftoverIndex] = Math.round((volumes[leftoverIndex] + leftover) * 1000) / 1000;
	return Object.fromEntries(months.map((m, i) => [m.key, volumes[i]]));
}

export interface VolumeAndPrice {
	months: Month[];
	volumKwh: number | null;
	/**
	 * Volum per måned (yyyy-MM → kWh) slik det sendes som volumPerMaanedKwh. Null bare når volumet mangler.
	 */
	monthlyVolumes: Record<string, number> | null;
	innpris: number | null;
	fortjeneste: number | null;
	pris: number | null;
}

/**
 * Beregner volum (fra totalvolum eller sum av per-måned-felter) og pris for opprett-veiviseren. Saksbehandler
 * oppgir kjøpspris (innpris) og kundepris (pris), og sikringsmarginen (fortjeneste) er differansen - det er
 * innpris og fortjeneste backend tar imot.
 */
export function calculateVolumeAndPrice(params: {
	startMonth: string;
	endMonth: string;
	volumeMode: "total" | "monthly";
	totalDistribution: TotalDistribution;
	totalVolume: string;
	volumePerMonth: Record<string, string>;
	innpris: string;
	kundepris: string;
}): VolumeAndPrice {
	const months = listMonths(firstDayOfMonth(params.startMonth), lastDayOfMonth(params.endMonth));
	let volumKwh: number | null;
	let monthlyVolumes: Record<string, number> | null = null;
	if (params.volumeMode === "monthly") {
		monthlyVolumes = Object.fromEntries(months.map((m) => [m.key, parseNorwegianNumber(params.volumePerMonth[m.key]) ?? 0]));
		const sum = Object.values(monthlyVolumes).reduce((acc, v) => acc + v, 0);
		volumKwh = sum > 0 ? sum : null;
	} else {
		volumKwh = parseNorwegianNumber(params.totalVolume);
		if (isDefined(volumKwh) && volumKwh > 0) {
			monthlyVolumes =
				params.totalDistribution === TotalDistribution.Standard ? distributeByStandardProfile(volumKwh, months) : distributeByHours(volumKwh, months);
		}
	}
	const innpris = parseNorwegianNumber(params.innpris);
	const pris = parseNorwegianNumber(params.kundepris);
	// Avrundet til tre desimaler, så flyttallsstøy fra subtraksjonen ikke sendes til backend.
	const fortjeneste = isDefined(innpris) && isDefined(pris) ? Math.round((pris - innpris) * 1000) / 1000 : null;
	return { months, volumKwh, monthlyVolumes, innpris, fortjeneste, pris };
}

/** Sikringens pris når backend ikke har beregnet den ennå: innpris + fortjeneste. */
export function priceOf(sikring: Sikring): number | null {
	if (isDefined(sikring.pris)) return sikring.pris;
	if (isDefined(sikring.innpris) && isDefined(sikring.fortjeneste)) return sikring.innpris + sikring.fortjeneste;
	return null;
}

const GENERAL_VALIDATION_MESSAGE = "Noen av opplysningene mangler eller er ugyldige. Kontroller stegene og prøv igjen.";

/** Egne valideringer i backend (med validationName) har norske meldinger. Bean Validation sine er engelske og erstattes. */
export function describeValidationErrors(errors: ValidationError[]): string {
	const ownMessages = errors.flatMap((e) => (e.validationName && e.message?.trim() ? [e.message.trim()] : []));
	// Også en tom liste, så brukeren aldri står uten forklaring.
	const needsGeneralMessage = ownMessages.length < errors.length || errors.length === 0;
	return [...ownMessages, ...(needsGeneralMessage ? [GENERAL_VALIDATION_MESSAGE] : [])].join(" · ");
}

export const INVALID_NUMBER_MESSAGE = "Ikke et gyldig tall. Bruk komma som desimalskilletegn, uten punktum.";

/** Et tomt felt er ikke ugyldig, bare ikke fylt ut. */
export function isInvalidNumber(value: string): boolean {
	return value.trim().length > 0 && parseNorwegianNumber(value) === null;
}

/** Prisfeltene tar maks 4 siffer før komma, altså opptil 9 999,99 øre/kWh. Flere siffer er en tastefeil. */
const MAX_PRICE_INTEGER_DIGITS = 4;

/** For bruk i onChange: endringen avvises når den gir for mange siffer før komma. */
export function exceedsPriceDigits(value: string): boolean {
	const integerPart = value.split(/[,.]/)[0];
	return integerPart.replace(/\D/g, "").length > MAX_PRICE_INTEGER_DIGITS;
}

/**
 * Fordeler en kolonne limt inn fra Excel på månedene, én linje per måned fra og med startKey. Gir null for en
 * enkelt verdi, så vanlig innliming i ett felt ikke overstyres. Celler kopiert fra Excel har et linjeskift til
 * slutt, som ikke er en måned - det har heller ikke linjer med bare mellomrom til slutt. Tomme linjer i midten
 * er tomme celler og tømmer måneden. Linjer etter siste måned i perioden forkastes.
 *
 * Bare én kolonne støttes. Med flere kolonner (tab mellom cellene) er det ikke gitt hvilken som er volumet, så
 * da gir den også null.
 */
export function pasteMonthColumn(text: string, months: Month[], startKey: string): Record<string, string> | null {
	const lines = text.replace(/\s+$/, "").split(/\r?\n/);
	if (lines.length < 2 || lines.some((line) => line.includes("\t"))) return null;
	const start = months.findIndex((m) => m.key === startKey);
	if (start === -1) return null;
	return Object.fromEntries(months.slice(start, start + lines.length).map((m, i) => [m.key, lines[i].trim()]));
}
